import style from './tree.module.css';
import {
  useRef,
  useCallback,
  useEffect,
  useState,
  useReducer,
  useLayoutEffect,
} from 'react';
import { useExpansionSet } from '../contexts/NodeExpansionSet';
import { TreeNodeData, NodeDropTargetLocation } from '../model';
import { NodeStat } from '../pathing';
import { cx } from '../utils/cx';
import { TreeNodeToolbar } from './TreeNodeToolbar';
import { Color } from './theme';
import { useTreeContext } from '../contexts/TreeContext';

export function TreeNode({
  node,
  path,
  isFinalChild,
  stats,
  currentTarget,
  isDragging,
  editNode,
  onSetEditNode,
  onDragStart,
}: {
  path: string;
  node: TreeNodeData;
  isDragging: boolean;
  isFinalChild: boolean;
  stats: Map<number, NodeStat>;
  currentTarget: null | {
    node_id: number;
    location: NodeDropTargetLocation;
  };
  editNode: number | null;
  onSetEditNode: (v: number | null) => void;
  onDragStart: (ref: HTMLElement, node: TreeNodeData) => void;
}) {
  const { expansionSet: expansion_set, expand, collapse } = useExpansionSet();
  const { setNodeText } = useTreeContext();
  const is_expanded = expansion_set.has(node.id);
  const nodeRef = useRef<HTMLDivElement>(null);
  const expandOnHoverTimer = useRef<number>(null);
  const children = node.children ?? [];
  const node_stats = stats.get(node.id);
  const is_target = currentTarget?.node_id === node.id;
  const target_location = currentTarget?.location ?? null;
  const can_expand = !is_expanded && children.length > 0;

  const [has_hover_state, setHasHoverState] = useState(false);
  const is_non_drag_hover = has_hover_state && !isDragging;
  const inputRef = useRef<HTMLInputElement | null>(null);

  const onNodeDragStart = useCallback(() => {
    if (!nodeRef.current) {
      return console.error('Missing node reference for drag');
    }
    onDragStart(nodeRef.current, node);
  }, []);

  /**
   * This value indicates that the current element is targeted
   * in a drag operation, and it should expand once the timer runs
   * out.
   */
  const is_expanding_hover =
    is_target && target_location === 'inside' && can_expand;

  useEffect(() => {
    if (expandOnHoverTimer.current) {
      window.clearTimeout(expandOnHoverTimer.current);
    }

    expandOnHoverTimer.current = null;

    if (is_expanding_hover) {
      expandOnHoverTimer.current = window.setTimeout(
        () => expand(node.id),
        1000,
      );
    }
  }, [is_expanding_hover, expand, node.id]);

  const onEditKey = useCallback(
    (ev: React.KeyboardEvent) => {
      if (ev.key === 'Enter' && ev.currentTarget instanceof HTMLInputElement) {
        setNodeText(node.id, ev.currentTarget.value);
        onSetEditNode(null);
      }
    },
    [node.id, setNodeText, onSetEditNode],
  );

  useLayoutEffect(() => {
    if (editNode === node.id && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editNode, node.id]);

  return (
    <>
      <div
        key={node.id}
        ref={nodeRef}
        data-id={node.id}
        data-path={path}
        data-depth={node_stats?.depth}
        data-index={node_stats?.indexInParent}
        data-colordex={((node_stats?.depth ?? 2) - 2) % 5}
        data-expanded={is_expanded}
        onMouseEnter={() => setHasHoverState(true)}
        onMouseLeave={() => setHasHoverState(false)}
        className={cx(
          style.node,
          isFinalChild && style.node_final_child,
          is_target && target_location === 'inside' && style.target_node,
        )}
      >
        {Array((node_stats?.depth ?? 1) - 1)
          .fill(0)
          .map((_, i) => (
            <div
              key={`${node.id}_${i}`}
              className={cx(
                style.node_position_indicator,
                `${style.node_position_indicator}_${i % 5}`,
              )}
            ></div>
          ))}
        <div className={`${style.node_head}`}>
          <div
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              width: '100%',
            }}
          >
            {is_target && target_location !== 'inside' && (
              <div
                style={{
                  ...{
                    position: 'absolute',
                    left: 0,
                    backgroundColor: Color[(node_stats?.depth ?? 2) - 2],
                    height: '2px',
                    width: '100%',
                  },
                  ...(target_location === 'after'
                    ? { bottom: '0' }
                    : { top: 0 }),
                }}
              ></div>
            )}
            <div
              data-id={node.id}
              data-depth={node_stats?.depth}
              data-index={node_stats?.indexInParent}
              data-expanded={is_expanded}
              className={style.node_content}
            >
              <div
                className={style.node_drag_handle}
                onPointerDown={onNodeDragStart}
              >
                <svg
                  width="15"
                  height="25"
                  viewBox="5 0 15 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9.5 8C10.3284 8 11 7.32843 11 6.5C11 5.67157 10.3284 5 9.5 5C8.67157 5 8 5.67157 8 6.5C8 7.32843 8.67157 8 9.5 8ZM9.5 14C10.3284 14 11 13.3284 11 12.5C11 11.6716 10.3284 11 9.5 11C8.67157 11 8 11.6716 8 12.5C8 13.3284 8.67157 14 9.5 14ZM11 18.5C11 19.3284 10.3284 20 9.5 20C8.67157 20 8 19.3284 8 18.5C8 17.6716 8.67157 17 9.5 17C10.3284 17 11 17.6716 11 18.5ZM15.5 8C16.3284 8 17 7.32843 17 6.5C17 5.67157 16.3284 5 15.5 5C14.6716 5 14 5.67157 14 6.5C14 7.32843 14.6716 8 15.5 8ZM17 12.5C17 13.3284 16.3284 14 15.5 14C14.6716 14 14 13.3284 14 12.5C14 11.6716 14.6716 11 15.5 11C16.3284 11 17 11.6716 17 12.5ZM15.5 20C16.3284 20 17 19.3284 17 18.5C17 17.6716 16.3284 17 15.5 17C14.6716 17 14 17.6716 14 18.5C14 19.3284 14.6716 20 15.5 20Z"
                    fill="#bbb"
                  />
                </svg>
              </div>
              {editNode === node.id ? (
                <input
                  type="text"
                  defaultValue={node.text}
                  data-nodeid={node.id}
                  ref={inputRef}
                  onKeyDown={onEditKey}
                />
              ) : (
                <span
                  className={style.nodecontent}
                  data-expandable={children.length > 0}
                  data-expanded={is_expanded}
                  onClick={() => onSetEditNode(node.id)}
                >
                  {node.text}{' '}
                </span>
              )}
              {node.children && node.children.length > 0 && (
                <button
                  className={style.expand_btn}
                  onClick={
                    is_expanded
                      ? () => collapse(node.id)
                      : can_expand
                        ? () => expand(node.id)
                        : undefined
                  }
                >
                  {!is_expanded
                    ? `+${stats.get(node.id)?.count ?? 0} children`
                    : 'collapse'}
                </button>
              )}
            </div>
            {is_non_drag_hover && <TreeNodeToolbar nodeId={node.id} />}
            {isDragging &&
              is_target &&
              target_location === 'inside' &&
              can_expand && (
                <svg
                  version="1.1"
                  id="circle"
                  xmlns="http://www.w3.org/2000/svg"
                  xmlnsXlink="http://www.w3.org/1999/xlink"
                  x="0px"
                  y="0px"
                  height="30"
                  width="30"
                  viewBox="0 0 30 30"
                  xmlSpace="preserve"
                  style={{ marginLeft: '1rem' }}
                >
                  <circle
                    fill="none"
                    stroke="#bbb"
                    strokeWidth="6"
                    strokeMiterlimit="0"
                    cx="-15"
                    cy="15"
                    r="12"
                    strokeDasharray="80"
                    strokeLinecap="round"
                    style={{ transform: 'rotate(-90deg)' }}
                  >
                    <animate
                      attributeName="stroke-dashoffset"
                      values="80;0"
                      dur="1s"
                      repeatCount="0"
                    ></animate>
                  </circle>
                </svg>
              )}
          </div>
        </div>
      </div>
      {is_expanded &&
        children.map((a, x) => (
          <TreeNode
            editNode={editNode}
            onSetEditNode={onSetEditNode}
            path={`${path},${a.id}`}
            key={a.id}
            stats={stats}
            node={a}
            onDragStart={onDragStart}
            currentTarget={currentTarget}
            isDragging={isDragging}
            isFinalChild={x === children.length - 1}
          />
        ))}
    </>
  );
}
